<template>
  <div class="page-container">
    <div class="max-w-7xl mx-auto p-6">
      <!-- Header Section -->
      <div class="bg-white border-round-2xl shadow-1 p-6 mb-6">
        <div class="flex align-items-center justify-content-between">
          <div class="flex align-items-center gap-4">
            <div class="header-icon-container">
              <i class="fa fa-file-text text-white text-2xl"></i>
            </div>
            <div>
              <h1 class="font-bold text-gray-800 text-3xl m-0 mb-2">{{ pageTitle }}</h1>
              <p class="text-gray-600 m-0 text-lg">{{ pageDescription }}</p>
            </div>
          </div>
          <Button
            v-if="activeTab < 7"
            :disabled="!appContentUpdated"
            @click="createOrUpdateEntity"
            :label="'Save ' + entityName"
            icon="fa fa-save"
            class="create-button"
          />
        </div>
      </div>

      <Loading
        v-if="loading"
        :full="false"
        :title="'Loading App Content Configurations...'"
      />

      <!-- Main Content -->
      <div v-else class="bg-white border-round-2xl shadow-1 overflow-hidden">
        <TabView v-model:activeIndex="activeTab" class="modern-tabview">
        <TabPanel header="Limitations">
          <div class="px-6 w-full">
            <p class="font-bold text-900 text-lg m-0" for="limitationInfo">
              Limitations info
            </p>
            <p
              class="m-0 text-sm line-height-2 text-700 mb-3"
              for="limitationInfo"
            >
              Information about the memberships limitations
            </p>
            <div class="mb-3">
              <LanguageSelector v-model="selectedLanguage" />
            </div>
            <HtmlEditor
              id="limitationInfo"
              name="limitationInfo"
              :showHeaders="true"
              @update:modelValue="
                (value) => updateTranslation('limitationsInfoText', value)
              "
              :modelValue="getTranslation('limitationsInfoText')"
              :editButton="true"
            />
          </div>
        </TabPanel>
        <TabPanel header="Membership">
          <div class="px-6 w-full">
            <p class="font-bold text-900 text-lg m-0">Membership info</p>
            <p class="m-0 text-sm text-700 mb-3">Summary of the membership</p>
            <div class="mb-3">
              <LanguageSelector v-model="selectedLanguage" />
            </div>
            <HtmlEditor
              id="membershipInfo"
              name="membershipInfo"
              :showHeaders="true"
              @update:modelValue="
                (value) => updateTranslation('membershipInfoText', value)
              "
              :modelValue="getTranslation('membershipInfoText')"
              :editButton="true"
            />
          </div>
        </TabPanel>
        <TabPanel header="Upgrade membership explainer">
          <div class="px-6 w-full">
            <p class="font-bold text-900 text-lg m-0">Upgrade membership explainer</p>
            <p class="m-0 text-sm text-700 mb-3">Information about upgrading membership</p>
            <div class="mb-3">
              <LanguageSelector v-model="selectedLanguage" />
            </div>
            <HtmlEditor
              id="upgradeMembershipInfo"
              name="upgradeMembershipInfo"
              :showHeaders="true"
              @update:modelValue="
                (value) => updateTranslation('upgradeMembershipExplainerText', value)
              "
              :modelValue="getTranslation('upgradeMembershipExplainerText')"
              :editButton="true"
            />
          </div>
        </TabPanel>
        <TabPanel header="Cancel membership explainer">
          <div class="px-6 w-full">
            <p class="font-bold text-900 text-lg m-0">Cancel membership explainer</p>
            <p class="m-0 text-sm text-700 mb-3">Information about cancelling membership</p>
            <div class="mb-3">
              <LanguageSelector v-model="selectedLanguage" />
            </div>
            <HtmlEditor
              id="cancelMembershipInfo"
              name="cancelMembershipInfo"
              :showHeaders="true"
              @update:modelValue="
                (value) => updateTranslation('cancelMembershipExplainerText', value)
              "
              :modelValue="getTranslation('cancelMembershipExplainerText')"
              :editButton="true"
            />
          </div>
        </TabPanel>
        <TabPanel header="Membership benefits">
          <div class="px-6 w-full">
            <p class="font-bold text-900 text-lg m-0">Membership benefits</p>
            <p class="m-0 text-sm text-700 mb-3">These reasons are shown to the user when they try to cancel membership</p>
            <div class="mb-3">
              <LanguageSelector v-model="selectedLanguage" />
            </div>
            <HtmlEditor
              id="cancelReasonsInfo"
              name="cancelReasonsInfo"
              :showHeaders="true"
              @update:modelValue="
                (value) => updateTranslation('cancelMembershipReasonsText', value)
              "
              :modelValue="getTranslation('cancelMembershipReasonsText')"
              :editButton="true"
            />
          </div>
        </TabPanel>
        <TabPanel header="Free points dialog">
          <div class="px-6 w-full">
            <p class="font-bold text-900 text-lg m-0">Free points dialog intro text</p>
            <p class="m-0 text-sm text-700 mb-3">Introduction text shown at the top of the free points dialog. Available tags are {first_name} and {points}</p>
            <div class="mb-3">
              <LanguageSelector v-model="selectedLanguage" />
            </div>
            <HtmlEditor
              id="freePointsDialogIntroInfo"
              name="freePointsDialogIntroInfo"
              :showHeaders="true"
              @update:modelValue="
                (value) => updateTranslation('freePointsDialogIntroText', value)
              "
              :modelValue="getTranslation('freePointsDialogIntroText')"
              :editButton="true"
            />
            
            <p class="font-bold text-900 text-lg m-0 mt-5">Free points dialog points body text</p>
            <p class="m-0 text-sm text-700 mb-3">Body text explaining the points in the free points dialog. Available tags are {first_name} and {points}</p>
            <div class="mb-3">
              <LanguageSelector v-model="selectedLanguage" />
            </div>
            <HtmlEditor
              id="freePointsDialogPointsBodyInfo"
              name="freePointsDialogPointsBodyInfo"
              :showHeaders="true"
              @update:modelValue="
                (value) => updateTranslation('freePointsDialogPointsBodyText', value)
              "
              :modelValue="getTranslation('freePointsDialogPointsBodyText')"
              :editButton="true"
            />
          </div>
        </TabPanel>
        <TabPanel header="External Links">
          <div class="px-6 w-full">
            <p class="font-bold text-900 text-lg m-0">Points Terms URL</p>
            <p class="m-0 text-sm text-700 mb-3">Configure the URL that opens when users click "Learn more" in the points info dialog</p>
            <div class="mb-3">
              <LanguageSelector v-model="selectedLanguage" />
            </div>
            <InputText
              id="pointsTermsUrl"
              name="pointsTermsUrl"
              class="w-full"
              placeholder="Type the url here"
              :modelValue="getTranslation('pointsTermsUrl')"
              @input="(event) => updateTranslation('pointsTermsUrl', event.target.value)"
            />
          </div>
        </TabPanel>
      </TabView>
      </div>
    </div>
  </div>
</template>
<script>
import { onMounted, ref } from "vue";
import HtmlEditor from "../../components/HtmlEditor.vue";
import LanguageSelector from "../../components/LanguageSelector.vue";
import InputText from "primevue/inputtext";
import { useStore } from "vuex";
import { useToast } from "primevue/usetoast";

export default {
  components: {
    HtmlEditor,
    LanguageSelector,
    InputText
  },

  setup() {
    const storeActionGetEntity = "appContents/getAppContent";
    const storeActionUpdateEntity = "appContents/update";
    const storeActionCreateEntity = "appContents/create";

    const storeGetterGetEntity = "appContents/appContent";

    const entityName = "App Content";
    const pageTitle = "App Content";
    const pageDescription = "Here you can manage the App contents";

    const store = useStore();
    const toast = useToast();

    const loading = ref(false);
    const activeTab = ref(0);
    const selectedLanguage = ref("en");

    const languages = [
      { label: "English", value: "en" },
      { label: "Danish", value: "da" },
      { label: "Finnish", value: "fi" },
      { label: "Norwegian", value: "no" },
    ];

    const appContent = ref();

    const editedAppContent = ref({
      limitationsInfoText: {
        translations: [
          { languageCode: "en", text: "" },
          { languageCode: "da", text: "" },
          { languageCode: "fi", text: "" },
          { languageCode: "no", text: "" },
        ],
      },
      membershipInfoText: {
        translations: [
          { languageCode: "en", text: "" },
          { languageCode: "da", text: "" },
          { languageCode: "fi", text: "" },
          { languageCode: "no", text: "" },
        ],
      },
      upgradeMembershipExplainerText: {
        translations: [
          { languageCode: "en", text: "" },
          { languageCode: "da", text: "" },
          { languageCode: "fi", text: "" },
          { languageCode: "no", text: "" },
        ],
      },
      cancelMembershipExplainerText: {
        translations: [
          { languageCode: "en", text: "" },
          { languageCode: "da", text: "" },
          { languageCode: "fi", text: "" },
          { languageCode: "no", text: "" },
        ],
      },
      cancelMembershipReasonsText: {
        translations: [
          { languageCode: "en", text: "" },
          { languageCode: "da", text: "" },
          { languageCode: "fi", text: "" },
          { languageCode: "no", text: "" },
        ],
      },
      pointsTermsUrl: {
        translations: [
          { languageCode: "en", text: "" },
          { languageCode: "da", text: "" },
          { languageCode: "fi", text: "" },
          { languageCode: "no", text: "" },
        ],
      },
      freePointsDialogIntroText: {
        translations: [
          { languageCode: "en", text: "" },
          { languageCode: "da", text: "" },
          { languageCode: "fi", text: "" },
          { languageCode: "no", text: "" },
        ],
      },
      freePointsDialogPointsBodyText: {
        translations: [
          { languageCode: "en", text: "" },
          { languageCode: "da", text: "" },
          { languageCode: "fi", text: "" },
          { languageCode: "no", text: "" },
        ],
      },
    });

    const appContentUpdated = ref(false);

    function getTranslation(field) {
      const translation = editedAppContent.value[field].translations.find(
        (t) => t.languageCode === selectedLanguage.value
      );
      return translation ? translation.text : "";
    }

    function updateTranslation(field, value) {
      const translation = editedAppContent.value[field].translations.find(
        (t) => t.languageCode === selectedLanguage.value
      );
      if (translation) {
        translation.text = value;
      }
      appContentUpdated.value = true;
    }

    function createOrUpdateEntity() {
      if (appContent.value == null) {
        createEntity();
      } else {
        updateEntity();
      }
    }

    async function updateEntity() {
      loading.value = true;
      await store
        .dispatch(storeActionUpdateEntity, editedAppContent.value)
        .then(() => {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: "App contents was updated",
            life: 3000,
          });
          appContentUpdated.value = false;
        })
        .catch((err) => {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: "Could not get app contents" + ": " + err,
            life: 3000,
          });
        })
        .finally(() => (loading.value = false));
    }

    async function createEntity() {
      loading.value = true;
      await store
        .dispatch(storeActionCreateEntity, editedAppContent.value)
        .then(() => {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: "App content configurations was created",
            life: 3000,
          });
          appContentUpdated.value = false;
        })
        .catch((err) => {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: "Could not create app contents" + ": " + err,
            life: 3000,
          });
        })
        .finally(() => (loading.value = false));
    }

    async function loadRequiredData() {
      loading.value = true;
      await store
        .dispatch(storeActionGetEntity)
        .then(() => {
          const loadedAppContent = store.getters[storeGetterGetEntity];
          if (loadedAppContent == null) {
            return;
          }
          appContent.value = loadedAppContent;
          
          // Merge loaded data with default structure to ensure consistent translation structure
          const mergedAppContent = { ...editedAppContent.value };
          
          // For each field, ensure only the 4 expected languages are maintained
          Object.keys(mergedAppContent).forEach(field => {
            if (loadedAppContent[field] && loadedAppContent[field].translations) {
              const expectedLanguages = ["en", "da", "fi", "no"];
              const mergedTranslations = expectedLanguages.map(langCode => {
                // Find existing translation for this language
                const existingTranslation = loadedAppContent[field].translations.find(
                  t => t.languageCode === langCode
                );
                return {
                  languageCode: langCode,
                  text: existingTranslation ? existingTranslation.text : mergedAppContent[field].translations.find(t => t.languageCode === langCode)?.text || ""
                };
              });
              mergedAppContent[field].translations = mergedTranslations;
            }
          });
          
          editedAppContent.value = mergedAppContent;
        })
        .catch((err) => {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: "Could not get app contents" + ": " + err,
            life: 3000,
          });
        })
        .finally(() => (loading.value = false));
    }

    onMounted(() => {
      loadRequiredData();
    });

    return {
      entityName,
      pageTitle,
      pageDescription,

      editedAppContent,
      appContentUpdated,
      loading,
      activeTab,
      selectedLanguage,
      languages,

      createOrUpdateEntity,
      updateTranslation,
      getTranslation,
    };
  },
};
</script>

<style scoped>
/* Fixed background that doesn't scroll - matching Rewards page */
.page-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%);
  background-attachment: fixed;
}

/* Header icon container - matching Rewards page */
.header-icon-container {
  background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%);
  padding: 0.75rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Create/Save button styling - matching Rewards page */
.create-button {
  background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%) !important;
  border: none !important;
  padding: 0.75rem 1.5rem !important;
  border-radius: 0.75rem !important;
  font-weight: 600 !important;
  color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
  transition: all 0.3s ease !important;
}

.create-button:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
  transform: translateY(-1px) !important;
}

.create-button:disabled {
  background: #9ca3af !important;
  transform: none !important;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
}

/* Modern TabView styling that matches the clean design language */
:deep(.modern-tabview .p-tabview-nav) {
  background: #f8fafc;
  border: none;
  border-bottom: 2px solid #e5e7eb;
  padding: 0 1.5rem;
  margin: 0;
  margin-bottom: 2rem;
  box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

:deep(.modern-tabview .p-tabview-nav-link) {
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  border-radius: 0;
  padding: 1.25rem 1.5rem;
  margin: 0;
  color: #6b7280;
  font-weight: 500;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: all 0.2s ease;
  position: relative;
}

:deep(.modern-tabview .p-tabview-nav-link:hover) {
  background: transparent;
  color: #374151;
  border-bottom-color: #d1d5db;
}

:deep(.modern-tabview .p-tabview-nav-link:focus) {
  box-shadow: none;
  outline: none;
}

/* Active tab styling - clean underline approach */
:deep(.modern-tabview .p-tabview-selected .p-tabview-nav-link) {
  background: transparent;
  color: #3b82f6;
  font-weight: 600;
  border-bottom-color: #3b82f6;
  transform: none;
  box-shadow: none;
}

:deep(.modern-tabview .p-tabview-selected .p-tabview-nav-link:hover) {
  background: transparent;
  color: #2563eb;
  border-bottom-color: #2563eb;
}

/* Tab content area - clean and spacious */
:deep(.modern-tabview .p-tabview-panels) {
  background: white;
  border: none;
  padding: 0;
}

:deep(.modern-tabview .p-tabview-panel) {
  background: white;
  padding: 0;
}

/* Remove default tabview styling */
:deep(.modern-tabview .p-tabview-nav-container) {
  position: relative;
}

:deep(.modern-tabview .p-tabview-ink-bar) {
  display: none;
}

/* Content padding */
:deep(.modern-tabview .p-tabview-panel .px-6) {
  padding: 2rem !important;
}

/* Form styling to match design language */
:deep(.p-inputtext) {
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

:deep(.p-inputtext:hover) {
  border-color: #9ca3af;
}

:deep(.p-inputtext:focus) {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

:deep(.p-dropdown) {
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
}

:deep(.p-dropdown:hover) {
  border-color: #9ca3af;
}

:deep(.p-dropdown:focus) {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Typography improvements */
.font-bold.text-gray-800 {
  color: #1f2937 !important;
}

.text-gray-600 {
  color: #6b7280 !important;
}

/* Responsive design */
@media (max-width: 768px) {
  :deep(.modern-tabview .p-tabview-nav-link) {
    padding: 0.75rem 1rem;
    font-size: 0.75rem;
  }
  
  :deep(.modern-tabview .p-tabview-nav) {
    padding: 0 1rem;
  }
  
  :deep(.modern-tabview .p-tabview-panel .px-6) {
    padding: 1rem !important;
  }
  
  .max-w-7xl {
    max-width: 100%;
  }
  
  .p-6 {
    padding: 1rem;
  }
}

/* Shadow utilities matching Rewards page */
.shadow-1 {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}
</style>
